import React, { useEffect } from "react";
import "./styles.scss";
import { RouteComponentProps } from "react-router-dom";
import {
  ActionButton,
  B2BSpinner,
  ButtonWrap,
  HotelPriceBreakdown,
  Icon,
  IconName,
  LoadingIndicator,
  EarnOfferBanner,
  getHSPEarnOfferDescription,
} from "halifax";
import {
  CardPaymentSelectors,
  getParentState,
  ParentState,
  RewardsPaymentSelectors,
  useCheckoutState,
  useCheckoutStateSelector,
  WalletSelectors,
} from "@capone/checkout";
import { Backdrop, Box, Link, Popover, Typography } from "@material-ui/core";
import { VIEWED_PACKAGE_CHECKOUT_BREAKDOWN } from "redmond";
import { PackagesPriceBreakdownConnectorProps } from "./container";
import { Event, TEvent } from "../../state/events";
import { PackagesMachineContext } from "../../state/types";
import {
  getBreakdownTotal,
  getPriceBreakdownPricingLineItems,
  getPriceBreakdownSummaryLineItems,
  getPriceBreakdownTotalLineItems,
} from "../../state/selectors/common";
import {
  AMOUNT_DUE,
  CHECKOUT_BREAKDOWN,
  TRIP_TOTAL_BREAKDOWN_TEXT,
} from "./textConstants";
import { CONFIRM_AND_BOOK } from "../textConstants";
import { formatCurrency, formatRewards } from "../../state/utils";
import { DesktopPackageBookValidationError } from "../DesktopPackagesBookWorkflow";
import { CART_UPDATE_TEXT } from "../MobilePackagesBookWorkflow/textConstants";
import { trackEvent } from "../../../../api/v0/trackEvent";
import dayjs from "dayjs";

export interface PackagesPriceBreakdownProps
  extends PackagesPriceBreakdownConnectorProps,
    RouteComponentProps {
  isMobile?: boolean;
  dropdown?: boolean;
  popover?: boolean;
  setValidationErrorTypes?: (
    types: DesktopPackageBookValidationError[]
  ) => void;
}

export const PackagesPriceBreakdown = ({
  isMobile,
  dropdown,
  popover,
  setValidationErrorTypes,
}: PackagesPriceBreakdownProps) => {
  const [openPopover, setOpenPopover] = React.useState<boolean>(false);

  const [state, send] = useCheckoutState<TEvent, PackagesMachineContext>();

  const parentState = getParentState(state.value) as ParentState;

  const pricingLineItems = useCheckoutStateSelector(
    getPriceBreakdownPricingLineItems
  );
  const totalLineItems = useCheckoutStateSelector(
    getPriceBreakdownTotalLineItems
  );
  const summaryLineItems = useCheckoutStateSelector(
    getPriceBreakdownSummaryLineItems
  );
  const isCostCoveredBySelectedPaymentMethods = useCheckoutStateSelector(
    CardPaymentSelectors.getIsCostCoveredBySelectedPaymentMethods
  );
  const breakdownTotal = useCheckoutStateSelector(getBreakdownTotal);
  const selectedRewardsAccountId = useCheckoutStateSelector(
    RewardsPaymentSelectors.getSelectedAccountId
  );
  const selectedPaymentMethodId = useCheckoutStateSelector(
    CardPaymentSelectors.getSelectedPaymentMethodId
  );
  const isCreditCardPaymentRequired = useCheckoutStateSelector(
    CardPaymentSelectors.getIsCreditCardPaymentRequired
  );
  const amountDue = useCheckoutStateSelector(
    CardPaymentSelectors.getTotalCardPaymentRequired
  );
  const offerToApply = useCheckoutStateSelector(
    WalletSelectors.getSelectedOffer
  );

  useEffect(() => {
    if (openPopover) {
      trackEvent({
        eventName: VIEWED_PACKAGE_CHECKOUT_BREAKDOWN,
        properties: { checkout_page: parentState },
      });
    }
  }, [openPopover]);

  const canContinue =
    isMobile ||
    (isCostCoveredBySelectedPaymentMethods && !!selectedRewardsAccountId);

  const handleContinue = () => {
    send({
      type: Event.SET_INITIAL_BOOK_TIME,
      initialTime: dayjs().toISOString(),
    });
    if (canContinue) {
      setValidationErrorTypes?.([]);
      send(Event.NEXT);
    } else {
      setValidationErrorTypes?.(
        (() => {
          switch (true) {
            case !selectedRewardsAccountId:
              return ["rewards"];
            case isCreditCardPaymentRequired && !selectedPaymentMethodId:
              return ["card"];
            default:
              return [];
          }
        })()
      );
    }
  };

  const togglePopover = () => {
    setOpenPopover((open) => !open);
  };

  const handleClose = () => {
    setOpenPopover(false);
  };

  if (isMobile) {
    if (!breakdownTotal) return null;

    const mobileContent = (
      <Box className="packages-price-breakdown-wrapper mobile">
        <Typography
          variant="h4"
          component="h2"
          className="price-breakdown-heading"
        >
          {CHECKOUT_BREAKDOWN}
        </Typography>
        <HotelPriceBreakdown
          pricingItems={pricingLineItems}
          totalItems={totalLineItems}
          summaryItems={summaryLineItems}
          isMobile
        />
        {popover ? (
          <ButtonWrap className="close-button" onClick={handleClose}>
            <Icon name={IconName.BlueX} />
          </ButtonWrap>
        ) : undefined}
      </Box>
    );

    if (dropdown) {
      return (
        <>
          <Link
            className="packages-price-breakdown-dropdown-root"
            onClick={togglePopover}
            component="button"
          >
            <Box className="total-price-label-container">
              <Typography
                className="trip-total"
                variant="subtitle2"
              >{`${TRIP_TOTAL_BREAKDOWN_TEXT}:`}</Typography>
              <Typography className="value fiat" variant="subtitle1">
                {formatCurrency(breakdownTotal.fiat)}
              </Typography>
              {breakdownTotal.rewards && (
                <>
                  <Typography className="separator" variant="subtitle2">
                    /
                  </Typography>
                  <Typography className="value rewards" variant="subtitle2">
                    {formatRewards(breakdownTotal.rewards)}
                  </Typography>
                </>
              )}
            </Box>

            <Icon name={IconName.Dropdown} className="total-price-label-icon" />
          </Link>
          <Popover
            className="packages-checkout-mobile-breakdown-popover"
            open={openPopover}
            PaperProps={{
              className: "price-breakdown-popover-paper",
            }}
            hideBackdrop
            onClose={handleClose}
            onClick={handleClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            disableEnforceFocus
          >
            {mobileContent}
          </Popover>
        </>
      );
    }

    if (popover) {
      return (
        <>
          <ButtonWrap
            className="packages-price-breakdown-popover-trigger"
            onClick={togglePopover}
          >
            <div className="total-price-label-container">
              <Typography
                className="total"
                variant="subtitle2"
              >{`${AMOUNT_DUE}:`}</Typography>
              <Typography className="value fiat" variant="subtitle1">
                {formatCurrency({ ...breakdownTotal.fiat, value: amountDue })}
              </Typography>
            </div>

            <div className="breakdown-label-container">
              <Typography className="breakdown-label">
                {CHECKOUT_BREAKDOWN}
              </Typography>
              <Icon name={IconName.PlusBlueCircle} className="breakdown-icon" />
            </div>
          </ButtonWrap>
          <Popover
            className="packages-checkout-mobile-breakdown-popover bottom"
            open={openPopover}
            PaperProps={{
              className: "price-breakdown-popover-paper",
            }}
            onClose={handleClose}
            onClick={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            disableEnforceFocus
            BackdropComponent={Backdrop}
            BackdropProps={{ invisible: false }}
          >
            {mobileContent}
          </Popover>
        </>
      );
    }

    return mobileContent;
  }
  return (
    <Box className="packages-price-breakdown-wrapper">
      <Typography
        variant="h4"
        component="h2"
        className="price-breakdown-heading"
      >
        {CHECKOUT_BREAKDOWN}
      </Typography>

      {[ParentState.cartUpdate, ParentState.cartUpdateForSeats].includes(
        parentState
      ) && (
        <Box className="packages-price-breakdown-loading-wrapper">
          <LoadingIndicator
            className="packages-checkout-loading-indicator"
            indicatorSize="small"
            indicator={B2BSpinner}
            message={CART_UPDATE_TEXT}
          />
        </Box>
      )}

      <HotelPriceBreakdown
        pricingItems={pricingLineItems}
        totalItems={totalLineItems}
        summaryItems={summaryLineItems}
        tooltipClassName="packages-price-breakdown-tooltip"
      />

      {getHSPEarnOfferDescription(offerToApply) && <EarnOfferBanner />}

      {[ParentState.cardPayment, ParentState.cartFulfill].includes(
        parentState
      ) && (
        <ActionButton
          onClick={handleContinue}
          message={CONFIRM_AND_BOOK}
          className="packages-continue-button book price-breakdown"
          disabled={parentState === ParentState.cartFulfill}
        />
      )}
    </Box>
  );
};
